import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, Phone, Mail, MapPin, ChevronRight } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebookF,
  faInstagram,
  faYoutube,
} from '@fortawesome/free-brands-svg-icons';
import WhatsAppIcon from './WhatsAppIcon';
import GoogleMapsIcon from './GoogleMapsIcon';
import { MOCK_NAKSHATRAS, MOCK_SITE_SETTINGS, getWhatsAppLink } from '../data/homamData';
import { isHomamSubdomain, getHomamPath, getMainSiteExternalUrl } from '../utils/domain';

export function HomamFooter() {
  const navigate = useNavigate();
  const isSub = isHomamSubdomain();
  const homePath = isSub ? '/' : '/nakshatra-homam';

  const whatsappHref = getWhatsAppLink({
    customMessage: `Namaste Sri Gnana Sai Baba Mandir Team 🙏\nI would like information on the 27 Nakshatra Maha Homam.`,
    source: 'Footer'
  });

  const scrollToSection = (id) => (e) => {
    e.preventDefault();
    if (window.location.pathname === homePath || window.location.pathname === `${homePath}/`) {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate(`${homePath}#${id}`);
    }
  };

  return (
    <footer className="homam-footer-sec">
      <div className="homam-container">
        {/* Top 4-Column Grid */}
        <div className="homam-footer-top-grid">
          {/* Col 1: About Mandir & Event */}
          <div style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <a
              href={getMainSiteExternalUrl('/')}
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 14 }}
              title="Visit Sri Gnana Sai Baba Mandir Main Website"
            >
              <div style={{ width: 70, height: 70, borderRadius: '50%', border: '2.5px solid #f5c542', background: '#102a4a', overflow: 'hidden', flexShrink: 0, boxShadow: '0 0 20px rgba(245, 197, 66, 0.45), 0 4px 12px rgba(0,0,0,0.3)' }}>
                <img
                  src="/images/sailogo.png"
                  alt="Sri Gnana Sai Baba Mandir"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.src = '/logo192.png'; }}
                />
              </div>
              <div>
                <h3 style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 800, color: '#ffffff', fontSize: 15, margin: 0, letterSpacing: 0.5, lineHeight: 1.3 }}>
                  SRI GNANA SAI BABA MANDIR
                </h3>
                <p style={{ fontFamily: 'Noto Sans Tamil, sans-serif', fontSize: 12, color: '#f5c542', margin: '3px 0 0 0', fontWeight: 600 }}>
                  {MOCK_SITE_SETTINGS.mandirNameTa}
                </p>
              </div>
            </a>

            <p style={{ fontSize: 12, color: '#e2e8f0', lineHeight: 1.6, margin: 0 }}>
              A 27-day monumental Vedic Yagna dedicated to honoring each of the 27 sacred lunar mansions. Experience profound planetary harmony, karmic purification, and divine grace under the auspicious blessings of Sri Gnana Sai Baba.
            </p>

            <div>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-mandir-seva"
                style={{ fontSize: 12, padding: '8px 14px' }}
              >
                <WhatsAppIcon size={16} style={{ color: '#25D366', marginRight: 6 }} />
                <span>Mandir WhatsApp Helpline</span>
              </a>
            </div>

            {/* Social Media - Follow Us */}
            <div style={{ marginTop: 2 }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#f5c542', letterSpacing: 0.8, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
                Follow Us
              </span>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <a
                  href="https://www.facebook.com/share/1HC1MpStwK/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: '#1877F2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'transform 0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                >
                  <FontAwesomeIcon icon={faFacebookF} style={{ fontSize: 15 }} />
                </a>
                <a
                  href="https://www.instagram.com/srignanasaibabamandir"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fd5949 45%, #d6249f 60%, #285aeb 90%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'transform 0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                >
                  <FontAwesomeIcon icon={faInstagram} style={{ fontSize: 15 }} />
                </a>
                <a
                  href="https://youtube.com/@srignanasaibabamandir"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="YouTube"
                  style={{ width: 34, height: 34, borderRadius: '50%', background: '#FF0000', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', textDecoration: 'none', transition: 'transform 0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.2)' }}
                >
                  <FontAwesomeIcon icon={faYoutube} style={{ fontSize: 15 }} />
                </a>
              </div>
            </div>
          </div>

          {/* Col 2: Quick Links */}
          <div style={{ textAlign: 'left' }}>
            <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: '#f5c542', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles className="w-3.5 h-3.5" />
              <span>Event Navigation</span>
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
              <li>
                <Link to={getHomamPath('/')} style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>Home & Event Overview</span>
                </Link>
              </li>
              <li>
                <a href="#find-star" onClick={scrollToSection('find-star')} style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>Find Your Birth Star Day</span>
                </a>
              </li>
              <li>
                <a href="#nakshatras" onClick={scrollToSection('nakshatras')} style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>27 Days Calendar Grid</span>
                </a>
              </li>
              <li>
                <Link to={getHomamPath('/today')} style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>Today's Live Homam Schedule</span>
                </Link>
              </li>
              <li>
                <Link to={getHomamPath('/gallery')} style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>Mandir & Darshan Gallery</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/homam-registration-direct"
                  style={{
                    color: '#0a192f',
                    background: 'linear-gradient(135deg, #f5c542 0%, #eab308 100%)',
                    fontWeight: 800,
                    fontSize: '11.5px',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 10px',
                    borderRadius: '6px',
                    boxShadow: '0 2px 8px rgba(245, 197, 66, 0.3)',
                    marginTop: '2px',
                    marginBottom: '2px',
                    lineHeight: '1.3'
                  }}
                >
                  <span style={{ color: '#0a192f' }}>27 Nakshatra Homam Registration</span>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#0a192f', marginLeft: '6px' }} />
                </Link>
              </li>
              <li>
                <Link to={getHomamPath('/contact')} style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>Homam Registration & Contact</span>
                </Link>
              </li>
              <li>
                <Link
                  to={`${getHomamPath('/contact')}#faq`}
                  onClick={() => {
                    if (window.location.pathname.includes('/contact')) {
                      setTimeout(() => {
                        document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 50);
                    }
                  }}
                  style={{ color: '#e2e8f0', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 6 }}
                >
                  <ChevronRight className="w-3.5 h-3.5" style={{ color: '#f5c542' }} />
                  <span>Frequently Asked Questions (FAQ)</span>
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Daily Mandir & Homam Timings */}
          <div style={{ textAlign: 'left' }}>
            <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: '#f5c542', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles className="w-3.5 h-3.5" />
              <span>Daily Mandir & Homam Timings</span>
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 11.5, color: '#e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Temple Open:</span>
                <strong style={{ color: '#f5c542' }}>05:50 AM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Kakad Aarti:</span>
                <strong style={{ color: '#f5c542' }}>06:15 AM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Gho Pooja:</span>
                <strong style={{ color: '#f5c542' }}>07:00 AM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Baba Abhishekam:</span>
                <strong style={{ color: '#f5c542' }}>07:30 AM</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#fde68a', fontWeight: 700 }}>Sahasranama Homam:</span>
                  <strong style={{ color: '#f5c542', fontWeight: 800 }}>09:30 AM – 11:00 AM</strong>
                </div>
                <span style={{ fontSize: 10.5, color: '#fde68a', opacity: 0.9 }}>
                  * Devotees requested to arrive by 09:00 AM
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Afternoon Aarti:</span>
                <strong style={{ color: '#f5c542' }}>12:00 PM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Annadhanam:</span>
                <strong style={{ color: '#f5c542' }}>12:30 PM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Dhoop Aarti:</span>
                <strong style={{ color: '#f5c542' }}>06:00 PM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: 4 }}>
                <span>Shej Aarti:</span>
                <strong style={{ color: '#f5c542' }}>08:30 PM</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>Temple Closes:</span>
                <strong style={{ color: '#f5c542' }}>09:00 PM</strong>
              </div>
            </div>
          </div>

          {/* Col 4: Venue & Contact */}
          <div style={{ textAlign: 'left' }}>
            <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 13, fontWeight: 700, color: '#f5c542', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Sparkles className="w-3.5 h-3.5" />
              <span>Mandir Location & Helpline</span>
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: 12, color: '#e2e8f0' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <MapPin className="w-4 h-4" style={{ color: '#f5c542', flexShrink: 0, marginTop: 2 }} />
                <span>
                  <strong style={{ display: 'block', color: '#ffffff' }}>{MOCK_SITE_SETTINGS.mandirName}</strong>
                  {MOCK_SITE_SETTINGS.venueAddress}, <br />{MOCK_SITE_SETTINGS.venueCity}
                </span>
              </div>
              <div style={{ paddingLeft: 24 }}>
                <a
                  href={MOCK_SITE_SETTINGS.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    color: '#ffd86b',
                    textDecoration: 'none',
                    fontSize: 12,
                    fontWeight: 600,
                    transition: 'opacity 0.2s ease'
                  }}
                >
                  <GoogleMapsIcon size={16} />
                  <span style={{ textDecoration: 'underline' }}>View on Google Maps</span>
                </a>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ffffff', fontWeight: 600 }}>
                  <Phone className="w-4 h-4" style={{ color: '#f5c542', flexShrink: 0 }} />
                  <span>Call Helplines:</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, paddingLeft: 24 }}>
                  {MOCK_SITE_SETTINGS.phoneNumbers.map((phone, idx) => (
                    <a
                      key={idx}
                      href={`tel:${phone.replace(/\s+/g, '')}`}
                      style={{ color: '#e2e8f0', textDecoration: 'none' }}
                    >
                      {phone}
                    </a>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Mail className="w-4 h-4" style={{ color: '#f5c542', flexShrink: 0 }} />
                <a href={`mailto:${MOCK_SITE_SETTINGS.email}`} style={{ color: '#e2e8f0', textDecoration: 'none' }}>
                  {MOCK_SITE_SETTINGS.email}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* 27 Nakshatras Matrix Links */}
        <div style={{ padding: '24px 0', borderBottom: '1px solid rgba(255,255,255,0.15)', textAlign: 'left' }}>
          <h4 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 11.5, fontWeight: 700, color: '#f5c542', textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 12 }}>
            Explore All 27 Sacred Nakshatras (அனைத்து 27 நட்சத்திரங்கள்)
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {MOCK_NAKSHATRAS.map((nak) => (
              <Link
                key={nak.id}
                to={getHomamPath(`/nakshatra/${nak.slug}`)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', borderRadius: 6, background: 'rgba(16, 42, 74, 0.9)', border: '1px solid rgba(245, 197, 66, 0.25)', color: '#e2e8f0', fontSize: 11, textDecoration: 'none' }}
              >
                <span style={{ color: 'rgba(245, 197, 66, 0.8)', fontFamily: 'monospace', fontSize: 9.5 }}>#{nak.orderIndex}</span>
                <span>{nak.nameEn}</span>
                <span style={{ fontFamily: 'Noto Sans Tamil, sans-serif', fontSize: 10, color: '#fde68a' }}>({nak.nameTa})</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom Disclaimer */}
        <div style={{ paddingTop: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, fontSize: 11.5, color: '#94a3b8' }}>
          <p style={{ margin: 0 }}>
            © {new Date().getFullYear()} {MOCK_SITE_SETTINGS.mandirName}. All rights reserved. Conducted in pure Vedic tradition.
          </p>
          <p style={{ margin: 0, color: '#cbd5e1' }}>
            Mandir Seva Trust • 27 Nakshatra Maha Homam
          </p>
        </div>
      </div>

      {/* 🌟 Floating Animated WhatsApp Button on ALL Nakshatra Portal Pages */}
      <div className="homam-floating-widget">
        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          className="homam-floating-whatsapp-btn"
          aria-label="Chat on WhatsApp"
          title="Chat with Mandir Seva Team on WhatsApp"
        >
          <WhatsAppIcon size={32} style={{ color: '#ffffff' }} />
        </a>
      </div>
    </footer>
  );
}

export default HomamFooter;
